import React from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { formatPrice } from 'utils/price/priceFormatter'
import type { ResolvedOption, SalesforceId } from 'models/purchaseFlowRevamp'
import { MUIDivider } from 'components/common/MUIDivider'

import { PricedCheckboxRow } from './PricedCheckboxRow.component'

interface Props {
  /** Options for the selected variant. */
  options: ResolvedOption[]
  /** Current option values keyed by option id. */
  values: Record<SalesforceId, boolean | number | string>
  /** Setter for a single option value. */
  onSetValue: (optionId: SalesforceId, value: boolean | number | string) => void
  /** Clears a single option value entirely (removes the key). */
  onClearValue: (optionId: SalesforceId) => void
  /**
   * Whether to print the list's own heading.
   *
   * The panel now renders this list twice — once for the products sold beside
   * the main one, under the section's own beige heading, and once for the
   * options OF the product, which the prototype leaves deliberately untitled
   * ("not a set of something else — it is part of what was already chosen").
   * Either way the heading belongs to the section, not to this component.
   */
  showHeading?: boolean
}

/**
 * Renders one input per option, switching on `option.kind`:
 *   - BOOLEAN → MUICheckbox (brand-styled, blue checked state)
 *   - NUMBER  → `MUIDropdownInput` (text type) for a bounded number input
 *   - ENUM    → `MUIDropdownInput` (selector type) with `isNativeSelect` children
 *
 * Using `MUIDropdownInput` aligns with the shared input component used across
 * legacy PF and the revamp entry surface (decision p55-5-02).
 *
 * Prices are shown inline next to each choice when a PBE exists.
 * Rows are separated by Dividers (U6 — Figma alignment).
 */
export const OptionPicker: React.FC<Props> = ({
  options,
  values,
  onSetValue,
  onClearValue,
  showHeading = true,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  if (options.length === 0) return null

  return (
    <Stack gap={1.2}>

      {showHeading &&
        <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
          {t('purchase_flow_revamp:drawer.options_label')}
        </Typography>
      }

      <Stack>
        {options.map((option) => (
        <React.Fragment key={option.id}>

          <Stack gap={0.4} sx={{ py: 1.2 }}>

            {option.kind === 'BOOLEAN' &&
              <PricedCheckboxRow
                checked={values[option.id] === true}
                onChange={(checked) => onSetValue(option.id, checked)}
                title={option.displayName}
                description={option.description}
                price={option.prices[0]}
              />
            }

            {option.kind === 'NUMBER' &&
              <Stack gap={0.4}>

                <Stack direction="row" alignItems="baseline" gap={0.6}>

                  <Typography variant="text-sm" color={GRAY_900}>
                    {option.displayName}
                    {option.required && ' *'}
                  </Typography>

                  {option.prices[0] &&
                    <Typography variant="text-xs" color={GRAY_700}>
                      {formatPrice(option.prices[0].unitPrice, option.prices[0].currency)}
                      {' / unit'}
                    </Typography>
                  }

                </Stack>

                <MUIDropdownInput
                  type={DropdownInputTypes.TEXT}
                  value={typeof values[option.id] === 'number' ? String(values[option.id]) : ''}
                  placeholder={option.required ? '0' : ''}
                  onChange={(e) => {
                    const raw = e.target.value
                    if (raw === '') {
                      onClearValue(option.id)
                      return
                    }
                    const parsed = Number(raw)
                    if (Number.isNaN(parsed)) return
                    if (option.min !== undefined && parsed < option.min) return
                    if (option.max !== undefined && parsed > option.max) return
                    onSetValue(option.id, parsed)
                  }}
                />

              </Stack>
            }

            {option.kind === 'ENUM' &&
              <Stack gap={0.4}>

                <Typography variant="text-sm" color={GRAY_900}>
                  {option.displayName}
                  {option.required && ' *'}
                </Typography>

                <MUIDropdownInput
                  type={DropdownInputTypes.SELECTOR}
                  isNativeSelect
                  value={typeof values[option.id] === 'string' ? (values[option.id] as string) : ''}
                  placeholder={t('purchase_flow_revamp:drawer.option_enum_placeholder')}
                  onChange={(e) => onSetValue(option.id, e.target.value)}
                >
                  <option value="" disabled>
                    {t('purchase_flow_revamp:drawer.option_enum_placeholder')}
                  </option>

                  {option.enumChoices?.map((choice) => (
                    <option key={choice.value} value={choice.value}>
                      {choice.label}
                      {choice.prices[0]
                        ? ` (+${formatPrice(choice.prices[0].unitPrice, choice.prices[0].currency)})`
                        : ''}
                    </option>
                  ))}

                </MUIDropdownInput>

              </Stack>
            }

          </Stack>

        </React.Fragment>
      ))}
      </Stack>

    </Stack>
  )
}
